// src/components/ledger/AccountsReceivable.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase/config';
import { 
  collection, getDocs, query, orderBy, where, doc, Timestamp, updateDoc
} from 'firebase/firestore';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const AccountsReceivable = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [orderItemsMap, setOrderItemsMap] = useState({});
  const [updatingOrder, setUpdatingOrder] = useState(false);

  useEffect(() => {
    fetchReceivablesData();
  }, []);

  const fetchReceivablesData = async () => {
    setLoading(true);
    try {
      const [clientsSnapshot, ordersSnapshot, paymentsSnapshot, itemsSnapshot] = await Promise.all([
        getDocs(collection(db, 'clients')),
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'payments')),
        getDocs(collection(db, 'orderItems'))
      ]);

      const clientsList = clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const itemsData = {};
      itemsSnapshot.docs.forEach(doc => {
        const item = { id: doc.id, ...doc.data() };
        if (!itemsData[item.orderId]) itemsData[item.orderId] = [];
        itemsData[item.orderId].push(item);
      });
      setOrderItemsMap(itemsData);

      const clientsWithBalance = clientsList.map(client => {
        const clientOrders = ordersSnapshot.docs
          .filter(doc => doc.data().clientId === client.id)
          .map(doc => ({ id: doc.id, ...doc.data() }));
        
        const totalOrdered = clientOrders.reduce((sum, order) => sum + (order.totalSellingPrice || 0), 0);
        const clientPayments = paymentsSnapshot.docs
          .filter(doc => doc.data().clientId === client.id)
          .map(doc => ({ id: doc.id, ...doc.data() }));
        const totalPaid = clientPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
        const balance = totalOrdered - totalPaid;
        
        const allOrders = clientOrders.map(order => ({
          ...order,
          items: itemsData[order.id] || [],
          workStatus: order.workStatus || 'pending'
        }));
        
        const workStatusCounts = { pending: 0, ongoing: 0, completed: 0 };
        allOrders.forEach(order => {
          const status = order.workStatus || 'pending';
          workStatusCounts[status]++;
        });
        
        return {
          ...client,
          totalOrdered,
          totalPaid,
          balance,
          orders: allOrders,
          pendingOrdersCount: allOrders.filter(o => o.balanceDue > 0).length,
          workStatusCounts,
          status: balance > 0 ? 'pending' : (totalOrdered > 0 ? 'paid' : 'no_orders')
        };
      });

      setClients(clientsWithBalance.filter(c => c.totalOrdered > 0));
    } catch (error) {
      console.error('Error fetching receivables data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    setUpdatingOrder(true);
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, { workStatus: newStatus, updatedAt: Timestamp.now() });
      await fetchReceivablesData();
      alert(`Order status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Error updating order status: ' + error.message);
    } finally {
      setUpdatingOrder(false);
    }
  };

  const getFilteredClients = useMemo(() => {
    let filtered = [...clients];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c => 
        c.name?.toLowerCase().includes(term) || 
        c.phone?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term)
      );
    }
    if (filterStatus === 'has_balance') {
      filtered = filtered.filter(c => c.balance > 0);
    } else if (filterStatus === 'paid') {
      filtered = filtered.filter(c => c.balance === 0 && c.totalOrdered > 0);
    }
    return filtered.sort((a, b) => b.balance - a.balance);
  }, [clients, searchTerm, filterStatus]);

  const getPaginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return getFilteredClients.slice(startIndex, startIndex + itemsPerPage);
  }, [getFilteredClients, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(getFilteredClients.length / itemsPerPage);
  const getTotalReceivables = () => getFilteredClients.reduce((sum, c) => sum + c.balance, 0);
  const getTotalPaid = () => getFilteredClients.reduce((sum, c) => sum + c.totalPaid, 0);
  const getTotalOrdered = () => getFilteredClients.reduce((sum, c) => sum + c.totalOrdered, 0);

  const generateReceivablesReport = () => {
    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.width;
    const filteredClients = getFilteredClients;
    
    doc.setFillColor(127, 26, 46);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text('ACCOUNTS RECEIVABLE REPORT', pageWidth / 2, 25, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 55);
    doc.text(`Total Clients with Orders: ${filteredClients.length}`, 20, 62);
    
    doc.setFillColor(240, 240, 240);
    doc.rect(20, 72, pageWidth - 40, 30, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('SUMMARY', 25, 82);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total Orders Value: ${getTotalOrdered().toLocaleString()} UGX`, 25, 92);
    doc.text(`Total Payments Received: ${getTotalPaid().toLocaleString()} UGX`, 25, 99);
    doc.text(`Total Outstanding Balance: ${getTotalReceivables().toLocaleString()} UGX`, 25, 106);
    
    const tableData = filteredClients.map(c => [
      c.name, c.phone || '-', c.email || '-',
      c.totalOrdered.toLocaleString(), c.totalPaid.toLocaleString(),
      c.balance.toLocaleString(), c.balance > 0 ? 'Pending' : 'Paid',
      `${c.workStatusCounts.pending}P/${c.workStatusCounts.ongoing}O/${c.workStatusCounts.completed}C`
    ]);
    
    autoTable(doc, {
      startY: 120,
      head: [['Client Name', 'Phone', 'Email', 'Total Orders', 'Total Paid', 'Balance', 'Payment Status', 'Work Status']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255] },
      styles: { fontSize: 8 },
      columnStyles: { 0: { cellWidth: 35 }, 1: { cellWidth: 20 }, 2: { cellWidth: 35 }, 3: { cellWidth: 22, halign: 'right' }, 4: { cellWidth: 22, halign: 'right' }, 5: { cellWidth: 22, halign: 'right' }, 6: { cellWidth: 18, halign: 'center' }, 7: { cellWidth: 20, halign: 'center' } }
    });
    doc.save(`Accounts_Receivable_Report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const generateClientStatement = (client) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    doc.setFillColor(127, 26, 46);
    doc.rect(0, 0, pageWidth, 45, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text('CLIENT STATEMENT', pageWidth / 2, 28, { align: 'center' });
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 60);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Client Information:', 20, 75);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Name: ${client.name}`, 20, 85);
    doc.text(`Phone: ${client.phone || 'N/A'}`, 20, 92);
    doc.text(`Email: ${client.email || 'N/A'}`, 20, 99);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Financial Summary:', 20, 115);
    doc.setFontSize(10);
    doc.text(`Total Orders Value: ${client.totalOrdered.toLocaleString()} UGX`, 20, 125);
    doc.text(`Total Payments Made: ${client.totalPaid.toLocaleString()} UGX`, 20, 132);
    doc.text(`Outstanding Balance: ${client.balance.toLocaleString()} UGX`, 20, 139);
    
    let yPos = 160;
    if (client.orders && client.orders.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Orders:', 20, yPos);
      yPos += 10;
      
      const ordersRows = client.orders.map(order => {
        const items = order.items || [];
        const productsList = items.map(item => `${item.productName} x${item.quantity}`).join(', ');
        const statusLabel = order.workStatus === 'completed' ? 'Completed' : order.workStatus === 'ongoing' ? 'Ongoing' : 'Pending';
        return [
          order.orderNumber || order.id.slice(-8),
          order.orderDate?.toDate ? new Date(order.orderDate.toDate()).toLocaleDateString() : 'N/A',
          productsList || '-',
          (order.totalSellingPrice || 0).toLocaleString(),
          (order.balanceDue || 0).toLocaleString(),
          statusLabel
        ];
      });
      
      autoTable(doc, {
        startY: yPos,
        head: [['Order #', 'Date', 'Products', 'Total (UGX)', 'Balance (UGX)', 'Work Status']],
        body: ordersRows,
        theme: 'striped',
        headStyles: { fillColor: [127, 26, 46], textColor: [255, 255, 255] },
        styles: { fontSize: 8 }
      });
    } else {
      doc.text('No orders found', 20, yPos);
    }
    doc.save(`${client.name}_Statement_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading accounts receivable...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>📋 Accounts Receivable</h1>
          <p style={styles.subtitle}>Track client payments, orders, and work status</p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.reportButton} onClick={generateReceivablesReport}>📄 Export Report</button>
        </div>
      </div>

      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryIcon}>💰</div>
          <div><div style={styles.summaryLabel}>Total Outstanding</div><div style={styles.summaryValue}>{getTotalReceivables().toLocaleString()} UGX</div></div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryIcon}>💳</div>
          <div><div style={styles.summaryLabel}>Total Collected</div><div style={styles.summaryValue}>{getTotalPaid().toLocaleString()} UGX</div></div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryIcon}>📊</div>
          <div><div style={styles.summaryLabel}>Total Orders</div><div style={styles.summaryValue}>{getTotalOrdered().toLocaleString()} UGX</div></div>
        </div>
      </div>

      <div style={styles.filtersBar}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Search</label>
          <input type="text" style={styles.filterInput} placeholder="Search by name, phone, email..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Payment Status</label>
          <select style={styles.filterSelect} value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
            <option value="all">All Clients</option>
            <option value="has_balance">Has Outstanding Balance</option>
            <option value="paid">Fully Paid</option>
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Total Clients</label>
          <span style={styles.filterCount}>{getFilteredClients.length}</span>
        </div>
      </div>

      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeaderRow}>
              <th style={styles.tableHeader}>Client</th>
              <th style={styles.tableHeader}>Contact</th>
              <th style={styles.tableHeader}>Total Orders</th>
              <th style={styles.tableHeader}>Amount Paid</th>
              <th style={styles.tableHeader}>Balance Due</th>
              <th style={styles.tableHeader}>Pending Orders</th>
              <th style={styles.tableHeader}>Work Status</th>
              <th style={styles.tableHeader}>Payment Status</th>
              <th style={styles.tableHeader}>Actions</th>
             </tr>
          </thead>
          <tbody>
            {getPaginatedData.map(client => {
              const { pending, ongoing, completed } = client.workStatusCounts;
              const workStatusParts = [];
              if (pending > 0) workStatusParts.push(`${pending} Pending`);
              if (ongoing > 0) workStatusParts.push(`${ongoing} Ongoing`);
              if (completed > 0) workStatusParts.push(`${completed} Completed`);
              const workStatusText = workStatusParts.join(', ') || 'No orders';
              const hasPendingWork = pending > 0;
              const workStatusColor = hasPendingWork ? '#f59e0b' : (ongoing > 0 ? '#3b82f6' : '#10b981');
              return (
                <tr key={client.id} style={styles.tableRow}>
                  <td style={{...styles.tableCell, fontWeight: '600'}}>
                    <div>{client.name}</div>
                    <div style={styles.clientEmail}>{client.email}</div>
                  </td>
                  <td style={styles.tableCell}>
                    {client.phone && <div>📞 {client.phone}</div>}
                    {client.address && <div>📍 {client.address}</div>}
                  </td>
                  <td style={{...styles.tableCell, fontWeight: '500'}}>{client.totalOrdered.toLocaleString()} UGX</td>
                  <td style={{...styles.tableCell, color: '#10b981', fontWeight: '600'}}>{client.totalPaid.toLocaleString()} UGX</td>
                  <td style={{...styles.tableCell, ...(client.balance > 0 ? styles.balanceDue : styles.balanceZero)}}>{client.balance.toLocaleString()} UGX</td>
                  <td style={styles.tableCell}>{client.pendingOrdersCount > 0 ? <span style={styles.pendingBadge}>{client.pendingOrdersCount} order(s)</span> : '-'}</td>
                  <td style={styles.tableCell}>
                    <span style={{ background: '#eef2ff', color: workStatusColor, padding: '4px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '500' }}>
                      {workStatusText}
                    </span>
                  </td>
                  <td style={styles.tableCell}>
                    <span style={{...styles.statusBadge, background: client.balance > 0 ? '#fee2e2' : '#d1fae5', color: client.balance > 0 ? '#dc2626' : '#059669'}}>
                      {client.balance > 0 ? '⚠️ Pending' : '✓ Paid'}
                    </span>
                  </td>
                  <td style={styles.tableCell}>
                    <button style={styles.viewButton} onClick={() => { setSelectedClient(client); setShowModal(true); }}>View Details</button>
                  </td>
                </tr>
              );
            })}
            {getPaginatedData.length === 0 && (
              <tr><td colSpan="9" style={styles.emptyState}>No clients with orders found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button style={{...styles.pageButton, ...(currentPage === 1 ? styles.pageButtonDisabled : {})}} onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}>Previous</button>
          <span style={styles.pageInfo}>Page {currentPage} of {totalPages}</span>
          <button style={{...styles.pageButton, ...(currentPage === totalPages ? styles.pageButtonDisabled : {})}} onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}>Next</button>
        </div>
      )}

      {/* Client Detail Modal */}
      {showModal && selectedClient && (
        <div style={modalStyles.overlay} onClick={() => setShowModal(false)}>
          <div style={{...modalStyles.container, maxWidth: '1100px'}} onClick={(e) => e.stopPropagation()}>
            <div style={modalStyles.header}>
              <h2 style={modalStyles.title}>{selectedClient.name}</h2>
              <div style={modalStyles.headerButtons}>
                <button style={modalStyles.printButton} onClick={() => generateClientStatement(selectedClient)}>🖨️ Print Statement</button>
                <button style={modalStyles.closeButton} onClick={() => setShowModal(false)}>×</button>
              </div>
            </div>
            <div style={modalStyles.content}>
              <div style={detailStyles.infoGrid}>
                <div style={detailStyles.infoCard}><div style={detailStyles.infoIcon}>📞</div><div><div style={detailStyles.infoLabel}>Phone</div><div style={detailStyles.infoValue}>{selectedClient.phone || 'N/A'}</div></div></div>
                <div style={detailStyles.infoCard}><div style={detailStyles.infoIcon}>✉️</div><div><div style={detailStyles.infoLabel}>Email</div><div style={detailStyles.infoValue}>{selectedClient.email || 'N/A'}</div></div></div>
                <div style={detailStyles.infoCard}><div style={detailStyles.infoIcon}>📍</div><div><div style={detailStyles.infoLabel}>Address</div><div style={detailStyles.infoValue}>{selectedClient.address || 'N/A'}</div></div></div>
              </div>

              <div style={detailStyles.financialSection}>
                <h3 style={detailStyles.sectionTitle}>Financial Summary</h3>
                <div style={detailStyles.financialGrid}>
                  <div style={detailStyles.financialCard}><div style={detailStyles.financialLabel}>Total Orders</div><div style={detailStyles.financialValue}>{selectedClient.totalOrdered.toLocaleString()} UGX</div></div>
                  <div style={detailStyles.financialCard}><div style={detailStyles.financialLabel}>Total Paid</div><div style={{...detailStyles.financialValue, color: '#10b981'}}>{selectedClient.totalPaid.toLocaleString()} UGX</div></div>
                  <div style={{...detailStyles.financialCard, background: '#fef3c7'}}><div style={detailStyles.financialLabel}>Balance Due</div><div style={{...detailStyles.financialValue, color: '#d97706'}}>{selectedClient.balance.toLocaleString()} UGX</div></div>
                </div>
              </div>

              {selectedClient.orders && selectedClient.orders.length > 0 && (
                <div style={detailStyles.tableSection}>
                  <h3 style={detailStyles.sectionTitle}>📦 Orders & Work Status</h3>
                  <div style={detailStyles.tableWrapper}>
                    <table style={detailStyles.sleekTable}>
                      <thead>
                        <tr>
                          <th style={detailStyles.sleekTableTh}>Order #</th>
                          <th style={detailStyles.sleekTableTh}>Date</th>
                          <th style={detailStyles.sleekTableTh}>Products</th>
                          <th style={detailStyles.sleekTableTh}>Total (UGX)</th>
                          <th style={detailStyles.sleekTableTh}>Balance (UGX)</th>
                          <th style={detailStyles.sleekTableTh}>Work Status</th>
                          <th style={detailStyles.sleekTableTh}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedClient.orders.map(order => {
                          const items = orderItemsMap[order.id] || [];
                          const productsList = items.map(item => `${item.productName} x${item.quantity}`).join(', ');
                          const workStatus = order.workStatus || 'pending';
                          const statusColor = workStatus === 'completed' ? '#10b981' : workStatus === 'ongoing' ? '#f59e0b' : '#ef4444';
                          const statusBg = workStatus === 'completed' ? '#d1fae5' : workStatus === 'ongoing' ? '#fef3c7' : '#fee2e2';
                          return (
                            <tr key={order.id}>
                              <td style={detailStyles.sleekTableTd}>{order.orderNumber || order.id.slice(-8)}</td>
                              <td style={detailStyles.sleekTableTd}>{order.orderDate?.toDate ? new Date(order.orderDate.toDate()).toLocaleDateString() : 'N/A'}</td>
                              <td style={detailStyles.sleekTableTd}>{productsList || '-'}</td>
                              <td style={{...detailStyles.sleekTableTd, fontWeight: '600'}}>{(order.totalSellingPrice || 0).toLocaleString()}</td>
                              <td style={{...detailStyles.sleekTableTd, color: '#d97706', fontWeight: '600'}}>{(order.balanceDue || 0).toLocaleString()}</td>
                              <td style={detailStyles.sleekTableTd}>
                                <span style={{ background: statusBg, color: statusColor, padding: '4px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '500' }}>
                                  {workStatus === 'completed' ? '✓ Completed' : workStatus === 'ongoing' ? '⏳ Ongoing' : '⚠️ Pending'}
                                </span>
                              </td>
                              <td style={detailStyles.sleekTableTd}>
                                <select value={workStatus} onChange={(e) => updateOrderStatus(order.id, e.target.value)} disabled={updatingOrder} style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '11px', cursor: 'pointer' }}>
                                  <option value="pending">⚠️ Pending</option>
                                  <option value="ongoing">⏳ Ongoing</option>
                                  <option value="completed">✓ Completed</option>
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {(!selectedClient.orders || selectedClient.orders.length === 0) && <div style={detailStyles.emptyState}>No orders found</div>}
            </div>
            <div style={modalStyles.footer}>
              <button style={modalStyles.closeButtonFooter} onClick={() => setShowModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { padding: '28px', background: '#f8fafc', minHeight: '100vh' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#64748b' },
  spinner: { width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#7f1a2e', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '16px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '16px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#0f172a', marginBottom: '4px' },
  subtitle: { fontSize: '13px', color: '#64748b' },
  headerActions: { display: 'flex', gap: '12px' },
  reportButton: { background: '#f1f5f9', color: '#1e293b', padding: '10px 20px', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '28px' },
  summaryCard: { background: '#ffffff', padding: '20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '16px', border: '1px solid #eef2f6' },
  summaryIcon: { fontSize: '32px' },
  summaryLabel: { fontSize: '11px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', marginBottom: '4px' },
  summaryValue: { fontSize: '20px', fontWeight: '700', color: '#0f172a' },
  filtersBar: { display: 'flex', gap: '20px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center', background: '#ffffff', padding: '16px', borderRadius: '12px', border: '1px solid #eef2f6' },
  filterGroup: { display: 'flex', alignItems: 'center', gap: '12px' },
  filterLabel: { fontSize: '13px', fontWeight: '500', color: '#475569' },
  filterInput: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', width: '200px', outline: 'none' },
  filterSelect: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: '#ffffff', cursor: 'pointer' },
  filterCount: { padding: '4px 12px', background: '#7f1a2e', color: '#ffffff', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  tableWrapper: { overflowX: 'auto', borderRadius: '12px', border: '1px solid #eef2f6', background: '#ffffff' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  tableHeaderRow: { background: '#f8fafc', borderBottom: '1px solid #eef2f6' },
  tableHeader: { textAlign: 'left', padding: '14px 16px', fontWeight: '600', color: '#475569' },
  tableRow: { borderBottom: '1px solid #f1f5f9' },
  tableCell: { padding: '14px 16px', color: '#334155' },
  clientEmail: { fontSize: '11px', color: '#94a3b8', marginTop: '4px' },
  balanceDue: { color: '#f59e0b', fontWeight: '600' },
  balanceZero: { color: '#10b981', fontWeight: '600' },
  pendingBadge: { background: '#fef3c7', color: '#d97706', padding: '4px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '500' },
  statusBadge: { display: 'inline-block', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '500' },
  viewButton: { padding: '6px 12px', background: '#3b82f6', color: '#ffffff', border: 'none', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '48px', color: '#94a3b8', fontSize: '13px' },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '24px', padding: '16px' },
  pageButton: { padding: '8px 16px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' },
  pageButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  pageInfo: { fontSize: '13px', color: '#64748b' }
};

const modalStyles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  container: { background: '#ffffff', borderRadius: '24px', width: '90%', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #eef2f6' },
  headerButtons: { display: 'flex', gap: '12px', alignItems: 'center' },
  printButton: { padding: '8px 16px', background: '#10b981', color: '#ffffff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  title: { fontSize: '20px', fontWeight: '600', color: '#0f172a' },
  closeButton: { background: 'none', border: 'none', fontSize: '28px', cursor: 'pointer', color: '#94a3b8' },
  content: { padding: '24px' },
  footer: { padding: '16px 24px', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  closeButtonFooter: { padding: '8px 20px', background: '#f1f5f9', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', color: '#475569' }
};

const detailStyles = {
  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
  infoCard: { display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #eef2f6' },
  infoIcon: { fontSize: '24px' },
  infoLabel: { fontSize: '11px', fontWeight: '500', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' },
  infoValue: { fontSize: '14px', fontWeight: '600', color: '#1e293b', marginTop: '4px' },
  financialSection: { marginBottom: '24px' },
  sectionTitle: { fontSize: '14px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' },
  financialGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' },
  financialCard: { textAlign: 'center', padding: '16px', background: '#f8fafc', borderRadius: '12px' },
  financialLabel: { fontSize: '11px', fontWeight: '500', color: '#64748b', marginBottom: '4px' },
  financialValue: { fontSize: '18px', fontWeight: '700', color: '#0f172a' },
  tableSection: { marginBottom: '24px' },
  tableWrapper: { overflowX: 'auto', borderRadius: '12px', border: '1px solid #eef2f6' },
  sleekTable: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  sleekTableTh: { textAlign: 'left', padding: '12px 16px', background: '#f8fafc', fontWeight: '600', color: '#475569', borderBottom: '1px solid #eef2f6' },
  sleekTableTd: { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', color: '#334155' },
  emptyState: { textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '13px' }
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleSheet);

export default AccountsReceivable;